import { Download, FileText, Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';

export interface EvidenceFile {
  filename: string;
  url?: string;
}

interface ReportData {
  adverseMedia?: {
    evidence?: Array<{
      filename?: string;
      url?: string;
      metadata?: { filename?: string };
    }>;
  };
  evidenceFiles?: Array<{ filename: string; url?: string }>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

/**
 * Extract evidence files from reportData, checking multiple possible locations.
 */
export function getEvidenceFiles(reportData: ReportData): EvidenceFile[] {
  const files: EvidenceFile[] = [];

  // Check adverseMedia.evidence for files with filename property
  if (reportData.adverseMedia?.evidence) {
    for (const item of reportData.adverseMedia.evidence) {
      if (item.filename) {
        files.push({ filename: item.filename, url: item.url });
      }
      // Also check metadata for filename
      if (item.metadata?.filename) {
        files.push({
          filename: item.metadata.filename as string,
          url: item.url,
        });
      }
    }
  }

  // Check Companies House officer matches for PDF evidence
  // Note: Try both camelCase (from screening service) and snake_case (from investigator)
  const companiesHouseOfficers =
    (reportData as Record<string, unknown>).companiesHouseOfficers ||
    reportData.companies_house_officers;
  if (companiesHouseOfficers) {
    for (const officer of companiesHouseOfficers as Array<{
      evidence?: { pdfPath?: string; sourceUrl?: string };
      officer?: { name?: string };
      company?: { name?: string };
    }>) {
      if (officer.evidence?.pdfPath) {
        const filename = officer.evidence.pdfPath.split('/').pop();
        if (filename) {
          files.push({
            filename,
            url: officer.evidence.sourceUrl,
          });
        }
      }
    }
  }

  // Check for evidence files stored at the top level
  if (reportData.evidenceFiles) {
    for (const file of reportData.evidenceFiles as EvidenceFile[]) {
      if (file.filename && !files.some((f) => f.filename === file.filename)) {
        files.push(file);
      }
    }
  }

  // Deduplicate by filename
  const seen = new Set<string>();
  return files.filter((file) => {
    if (seen.has(file.filename)) return false;
    seen.add(file.filename);
    return true;
  });
}

interface EvidencePackageSectionProps {
  files: EvidenceFile[];
  onDownload: (filename: string) => void;
  onDownloadAll: () => void;
  isDownloadingAll: boolean;
  disabled?: boolean;
}

export function EvidencePackageSection({
  files,
  onDownload,
  onDownloadAll,
  isDownloadingAll,
  disabled = false,
}: EvidencePackageSectionProps) {
  if (disabled) {
    return (
      <section className="mb-8 rounded-lg border bg-white p-6 opacity-60 shadow-sm">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-400">Evidence Package</h2>
          <span className="rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-400">
            Coming Soon
          </span>
        </div>
        <p className="text-sm text-gray-400">
          Request and collect supporting documents from your clients via secure upload links. This
          feature will be available soon.
        </p>
      </section>
    );
  }

  return (
    <section className="mb-8 rounded-lg border bg-white p-6 shadow-sm">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-lg font-semibold text-[#051923]">Evidence Package</h2>
        {files.length > 0 && (
          <Button
            variant="outline"
            size="sm"
            className="text-sm"
            onClick={onDownloadAll}
            disabled={isDownloadingAll}
          >
            {isDownloadingAll ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Download className="mr-2 h-4 w-4" />
            )}
            {isDownloadingAll ? 'Downloading...' : 'Download All'}
          </Button>
        )}
      </div>
      {files.length > 0 ? (
        <>
          <p className="mb-4 text-sm text-gray-600">
            Supporting documents collected during screening, including PDF snapshots of adverse
            media source articles and Companies House officer appointment records.
          </p>
          <ul className="space-y-2">
            {files.map((file) => (
              <li key={file.filename} className="flex items-center justify-between gap-4 py-2">
                <div className="flex min-w-0 flex-1 items-center gap-2">
                  <FileText className="h-5 w-5 shrink-0 text-gray-400" />
                  <span className="break-all text-sm">{file.filename}</span>
                </div>
                <button
                  type="button"
                  onClick={() => onDownload(file.filename)}
                  className="flex shrink-0 items-center gap-1 p-2 text-sm text-blue-600 hover:underline"
                >
                  <Download className="h-5 w-5" />
                </button>
              </li>
            ))}
          </ul>
        </>
      ) : (
        <p className="text-sm text-gray-500">
          No evidence files were generated for this assessment. PDF snapshots of source articles are
          captured when adverse media screening finds relevant results in Enhanced Due Diligence
          mode.
        </p>
      )}
    </section>
  );
}
